import { useMemo } from 'react';
import date from 'date-and-time';
import { SerializedScheduledPost } from '../promote/types';

export interface PostsDay {
  /** "YYYY-MM-DD" of the day this bar counts. */
  key: string;
  /** Midnight of that day, for a formatted label. */
  at: Date;
  /** Posts that went out that day. A real zero, not a gap. */
  count: number;
}

export interface PostsPerDay {
  days: PostsDay[];
  /** Posts that went out across the whole window. */
  total: number;
  /** The busiest day's count, so the bars can be scaled. */
  peak: number;
}

/**
 * Posted counts per day, over the last `windowDays` days.
 *
 * Only `posted` rows count: a scheduled post has not gone out, and drawing it
 * as one would overstate what the artist has published.
 */
export function usePostsPerDay(posts: SerializedScheduledPost[] | undefined, windowDays: number): PostsPerDay {
  return useMemo(() => {
    const counts = new Map<string, number>();
    (posts ?? []).forEach((post) => {
      if (post.status !== 'posted' || post.postedAt === null) {
        return;
      }
      const key = date.format(new Date(post.postedAt), 'YYYY-MM-DD');
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });

    const today = new Date();
    const days: PostsDay[] = [];
    for (let offset = windowDays - 1; offset >= 0; offset -= 1) {
      const at = date.addDays(today, -offset);
      const key = date.format(at, 'YYYY-MM-DD');
      days.push({ key, at, count: counts.get(key) ?? 0 });
    }

    const total = days.reduce((sum, day) => sum + day.count, 0);
    const peak = days.reduce((max, day) => Math.max(max, day.count), 0);

    return { days, total, peak };
  }, [posts, windowDays]);
}
